import { useCallback, useEffect, useState } from "react";
import { api } from "@/api/client";
import { useAuth } from "@/auth/AuthContext";
import type { components } from "@dolfin/api-public";

type BillResponse = components["schemas"]["BillResponse"];
type BillState = components["schemas"]["BillState"];

export interface ApBills {
  loading: boolean;
  error: string | null;
  bills: BillResponse[];
  refresh: () => Promise<void>;
}

/** First page of bills in one state, newest first, behind the AP overview cards. */
export function useApBills(state: BillState, limit = 5): ApBills {
  const { activeOrganisationId } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [bills, setBills] = useState<BillResponse[]>([]);

  const fetchBills = useCallback(async () => {
    if (!activeOrganisationId) return;
    setLoading(true);
    setError(null);

    try {
      const res = await api.GET("/v1/bills", {
        params: { query: { Limit: limit, States: [state] } },
      });

      if (res.error) {
        setError("Failed to load bills");
      }

      setBills(res.data?.data ?? []);
    } catch (e) {
      console.error("Failed to load bills:", e);
      setError("Failed to load bills");
    } finally {
      setLoading(false);
    }
  }, [activeOrganisationId, state, limit]);

  useEffect(() => {
    void fetchBills();
  }, [fetchBills]);

  return { loading, error, bills, refresh: fetchBills };
}
